import { useCallback, useEffect, useRef } from 'react';

export function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback);
  const savedDelay = useRef(delay);

  const intervalId = useRef<NodeJS.Timer | undefined>(undefined);

  const stop = useCallback(() => {
    if (!intervalId.current) return;

    clearInterval(intervalId.current);
    intervalId.current = undefined;
  }, []);

  const start = useCallback(
    (delay: number | null = savedDelay.current) => {
      // stop interval if already running
      if (intervalId.current) stop();

      /**
       * Don't schedule if no delay is specified.
       * Note: 0 is a valid value for delay.
       */
      if (!delay || delay < 0) return;

      savedDelay.current = delay;
      intervalId.current = setInterval(() => savedCallback.current(), delay);
    },
    [stop]
  );

  // Remember the latest callback if it changes.
  useEffect(
    function updateCallback() {
      savedCallback.current = callback;
    },
    [callback]
  );

  useEffect(
    function initInterval() {
      // do nothing if already running
      if (intervalId.current) return;

      // auto start the timer
      start();

      // stop timer on component unmount or rerender
      return () => {
        stop();
      };
    },
    [start, stop]
  );

  useEffect(
    function resetInterval() {
      savedDelay.current = delay;

      // do nothing if timer not running
      if (!intervalId.current) return;

      start(delay);
    },
    [delay, start, stop]
  );

  return { start, stop };
}
